import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSegmentSchemaCatalog } from '../SegmentSchemaCatalog';

const FIELD_VALUE_LIMIT = 50;
const FIELD_VALUE_SEARCH_DEBOUNCE_MS = 250;
const FIELD_VALUE_STALE_TIME_MS = 5 * 60 * 1000;
const FIELD_VALUE_GC_TIME_MS = 10 * 60 * 1000;

const observedFieldValueKeys = {
  all: ['segment-field-values'] as const,
  field: (schemaId: string, field: string) => [...observedFieldValueKeys.all, schemaId, field] as const,
  search: (schemaId: string, field: string, search: string) =>
    [...observedFieldValueKeys.field(schemaId, field), 'search', search] as const,
};

interface UseObservedFieldValuesArgs {
  schemaId: string;
  field: string;
  search: string;
  enabled: boolean;
}

interface ObservedFieldValuesResult {
  suggestedOptions: string[];
  observedValuesAreLoading: boolean;
}

function warnFieldValueLoadFailure(error: unknown, schemaId: string, field: string) {
  console.warn({ error, schemaId, field }, 'Failed to load observed segment field values');
}

/** Load and cache observed values for one segment field when its input is active. */
export function useObservedFieldValues({
  schemaId,
  field,
  search,
  enabled,
}: UseObservedFieldValuesArgs): ObservedFieldValuesResult {
  const { getSchemaById, getFieldValues } = useSegmentSchemaCatalog();
  const schemaFieldInfo = getSchemaById(schemaId)?.fields.find(
    schemaField => schemaField.name === field,
  );
  const fieldSupportsObservedValues =
    schemaFieldInfo?.type === 'string' ||
    (schemaFieldInfo?.type === 'array' && schemaFieldInfo.itemType === 'string');
  const observedValuesShouldLoad =
    !!getFieldValues && fieldSupportsObservedValues && !schemaFieldInfo?.enum?.length;
  const queryIsEnabled = enabled && observedValuesShouldLoad;
  const normalizedSearch = search.trim();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (!queryIsEnabled) {
      setDebouncedSearch('');
      return;
    }

    const searchTimer = window.setTimeout(
      () => setDebouncedSearch(normalizedSearch),
      FIELD_VALUE_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(searchTimer);
  }, [normalizedSearch, queryIsEnabled]);

  const initialQuery = useQuery({
    queryKey: observedFieldValueKeys.field(schemaId, field),
    queryFn: async () => {
      try {
        return await getFieldValues!({ schemaId, field, limit: FIELD_VALUE_LIMIT });
      } catch (error) {
        warnFieldValueLoadFailure(error, schemaId, field);
        throw error;
      }
    },
    enabled: queryIsEnabled,
    retry: false,
    staleTime: FIELD_VALUE_STALE_TIME_MS,
    gcTime: FIELD_VALUE_GC_TIME_MS,
  });

  const normalizedCurrentSearch = normalizedSearch.toLowerCase();
  const normalizedDebouncedSearch = debouncedSearch.toLowerCase();
  const debouncedSearchIsAlreadyObserved = initialQuery.data?.values.some(
    value => value.toLowerCase() === normalizedDebouncedSearch,
  );
  const observedValuesShouldBeSearched =
    queryIsEnabled &&
    !!initialQuery.data?.truncated &&
    !!normalizedDebouncedSearch &&
    !debouncedSearchIsAlreadyObserved;
  const searchQuery = useQuery({
    queryKey: observedFieldValueKeys.search(schemaId, field, normalizedDebouncedSearch),
    queryFn: async () => {
      try {
        return await getFieldValues!({
          schemaId,
          field,
          search: debouncedSearch,
          limit: FIELD_VALUE_LIMIT,
        });
      } catch (error) {
        warnFieldValueLoadFailure(error, schemaId, field);
        throw error;
      }
    },
    enabled: observedValuesShouldBeSearched,
    retry: false,
    staleTime: FIELD_VALUE_STALE_TIME_MS,
    gcTime: FIELD_VALUE_GC_TIME_MS,
  });
  const currentSearchIsAlreadyObserved = initialQuery.data?.values.some(
    value => value.toLowerCase() === normalizedCurrentSearch,
  );
  const searchIsDebouncing =
    queryIsEnabled &&
    !!initialQuery.data?.truncated &&
    !!normalizedCurrentSearch &&
    !currentSearchIsAlreadyObserved &&
    normalizedCurrentSearch !== normalizedDebouncedSearch;

  const suggestedOptions = Array.from(
    new Set([...(initialQuery.data?.values ?? []), ...(searchQuery.data?.values ?? [])]),
  );

  return {
    suggestedOptions,
    observedValuesAreLoading: initialQuery.isFetching || searchIsDebouncing || searchQuery.isFetching,
  };
}
